import { ComponentProps, ReactNode } from 'react'
import InfiniteScroll from 'react-infinite-scroller'
import { GetNextPageParamFunction, useInfiniteQuery, UseInfiniteQueryOptions } from 'react-query'

type UseInfiniteScrollerProps<T> = {
  queryKey: any[]
  queryFunction: (params: { pageParam?: any }) => Promise<T>
  enabled?: boolean
  queryOptions?: UseInfiniteQueryOptions<T, Error> & {
    getNextPageParam?: GetNextPageParamFunction<T>
  }
  renderItem?: (item: T, index: number, refetchPage: () => void) => JSX.Element
  loader?: ReactNode
}

type InfiniteScrollProps = Omit<ComponentProps<typeof InfiniteScroll>, 'loadMore' | 'hasMore'>

type DefaultParamsInResponse = {
  hasMore?: boolean
  cursorId?: any
}

const defaultGetNextPageParam: GetNextPageParamFunction<DefaultParamsInResponse> = (lastPage) =>
  lastPage?.hasMore ? { cursorId: lastPage.cursorId } : null

// eslint-disable-next-line @typescript-eslint/no-unnecessary-type-constraint
export const useInfiniteScroller = <T extends unknown>({
  queryKey,
  queryFunction,
  enabled = true,
  queryOptions = {},
}: UseInfiniteScrollerProps<T>) => {
  const query = useInfiniteQuery<T, Error>(queryKey, ({ pageParam }) => queryFunction({ pageParam }), {
    enabled,
    getNextPageParam: queryOptions.getNextPageParam ?? (defaultGetNextPageParam as GetNextPageParamFunction<T>),
    ...queryOptions,
  })

  const handleNextPage = () => {
    if (query.hasNextPage && !query.isFetchingNextPage) {
      query.fetchNextPage()
    }
  }
  const InfiniteScrollComponent = ({ children, loader, ...rest }: InfiniteScrollProps) => (
    <InfiniteScroll
      pageStart={0}
      loadMore={handleNextPage}
      hasMore={query.hasNextPage}
      loader={
        loader || (
          <div className='loader' key={0}>
            Loading...
          </div>
        )
      }
      {...rest}
    >
      {children}
    </InfiniteScroll>
  )

  return {
    data: query.data?.pages || [],
    query,
    handleNextPage,
    hasNextPage: query.hasNextPage,
    refetch: query.refetch,
    InfiniteScrollComponent,
  }
}
